import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import styles from "./QuizPreviewModal.module.scss";
import { Program, ExtendedQuiz } from "@types";
import UnitModal from "@src/components/unit-progress-modal/modal/UnitModal";
import { useToast, useCourseContent } from "@src/hooks";
import Spinner from "@src/components/spinner/Spinner";
import { FeedbackFormContent, Quiz, useGetQuizLazyQuery } from "@src/api/graphql-codegen";
import { useMediaQuery } from "react-responsive";
import FSBackIcon from 'src/assets/icons/arrow_back.svg?react';
import { MenuOutlined, CloseOutlined } from '@ant-design/icons';
import SelectedContent from "@src/components/program-details/selected-content/SelectedContent";
import QuizSidebar from "./QuizSidebar";
import GenerateExitTicketLinkModal from "@src/components/program-details/exitticket-modal/GenerateExitTicketLinkModal";
import { createURL } from "@src/constants";

type Props = {
  open: boolean;
  onClose: () => void;
  quiz: ExtendedQuiz;
  program?: Program;
  preview?: boolean;
};

function QuizPreviewModal(props: Props) {
  const {
    open,
    onClose,
    quiz,
    preview,
  } = props;

  const {
    content: selectedContent,
    isPreview,
    setIsPreview,
    setIsStudentQuiz,
    setStudentQuiz,
    showExitModal,
    setShowExitModal,
    setContent,
    currentQuiz,
    setCurrentQuiz,
    myQuizzes,
    setMyQuizzes,
  } = useCourseContent();

  const [showMenu, setShowMenu] = useState(false);
  const [showQuizView, setShowQuizView] = useState(false);

  const isTablet = useMemo(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    return /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(userAgent)
  }, []);
  const isMobile = useMediaQuery({ maxWidth: 1024 });
  const { successToast, errorToast } = useToast();
  let countDownTimer = useRef<NodeJS.Timeout | null>(null);

  const [getQuiz, {data, loading}] = useGetQuizLazyQuery({ fetchPolicy: 'network-only',
    onCompleted(data) {
      if (data.getQuiz) {
        if (!preview) {
          setShowQuizView(true)
        }
        setIsPreview(preview ?? false);
        setIsStudentQuiz(true);
        setStudentQuiz(data.getQuiz)
        if (data.getQuiz.contents) {
          setContent(data.getQuiz.contents.at(0))
        }
        setCurrentQuiz(quiz)
      } else {
        setIsStudentQuiz(false);
        errorToast("Can't preview Assessment, Please try again");
        onClose();
      }      
    },
  })

  useEffect(() => {
    if (quiz.id) {
      getQuiz({
        variables: { input: { id: quiz.id } },
      })
    }
  }, [quiz, getQuiz])

  useEffect(() => {
    const expire = currentQuiz?.expiryMinutes ?? 0
    if (expire > 0) {
      countDownTimer.current = setInterval(() => {
        const tempQuiz = {
          ...currentQuiz,
          expiryMinutes: (currentQuiz?.expiryMinutes ?? 0) - 60,
          status: (currentQuiz?.expiryMinutes ?? 0) >= 0 ? true : false,
        };
        setCurrentQuiz(tempQuiz as ExtendedQuiz);
      }, 60 * 1000); // countdown every 1 min
    }
    return () => {
      if (countDownTimer.current) {
        clearInterval(countDownTimer.current);
        countDownTimer.current = null;
      }
    };
  }, [currentQuiz, setCurrentQuiz]);

  const handleMenu = useCallback(() => {
    if (isTablet || isMobile) {
      setShowMenu(current => !current)
    } else {
      onClose();
      setIsStudentQuiz(false)
    }
  }, [isMobile, isTablet, onClose, setIsStudentQuiz]);

  const handleCloseMenu = () => {
    setShowMenu(false)
  }

  const handleActiveFormPreview = (feedback: FeedbackFormContent, expiration: any) => {
    const tempQuiz = {
      ...currentQuiz,
      feedbackForm: feedback,
      expiryMinutes: expiration * 60,
      status: true,
    }
    setCurrentQuiz(tempQuiz as ExtendedQuiz)
    const updateQuizzes = myQuizzes.map((quiz: any, i: number) => {
      if (quiz.id === tempQuiz?.id) {
        return {
          ...quiz,
          feedbackForm: feedback,
          expiryMinutes: expiration * 60,
          status: true,
        }
      } else {
        return quiz
      }
    })
    setMyQuizzes(updateQuizzes);
    const url = createURL(feedback.link ?? "");
    window.navigator.clipboard.writeText(url);
    successToast("Quiz Link Successfully Created and Link copied successfully!");
    setShowExitModal(false);
  }

  const handleCloseQuiz = () => {
    setShowQuizView(false)
    onClose()
  }

  const listStyle = useMemo(() => {
    if (isTablet || isMobile) {
      return styles.listMenu;
    } else {
      return styles.list;
    }
  }, [isTablet, isMobile])

  const screenWidth = useMemo(() => (isTablet || isMobile) ? -window.innerWidth : 0, [isMobile, isTablet]);

  const listSection = useMemo(() =>
  (<div className={listStyle} style={{transform: showMenu ? 'translateX(0)' : `translateX(${screenWidth}px)`}}>
    { (isTablet || isMobile) && showMenu && <div className={styles.menuBar}>
      <button onClick={handleMenu} className={styles.menuButton} type={'button'}>
        <div className={styles.menuWrapper}>
          <CloseOutlined className={styles.burgerIcon}/>
        </div>
      </button>
      <button onClick={onClose} className={styles.closeButton} type={'button'}>
        <div className={styles.menuWrapper}>
          <FSBackIcon className={styles.closeIcon}/><div>Go Back</div>
        </div>
      </button>
    </div>}
    <QuizSidebar quiz={quiz as Quiz} quizContent={data?.getQuiz} closeNav={handleCloseMenu} />
  </div>),
  [handleMenu, isMobile, isTablet, quiz, listStyle, onClose, screenWidth, showMenu, data?.getQuiz]
);

  return (
    <UnitModal
      open={open}
      onClose={onClose}
      isLoading={loading}
    >
      { loading ? (
        <div className={styles.loading}>
          <Spinner />
        </div>
      ) : (
        <div className={styles.wrapper} > 
          <div className={styles.teacherFSView}>
            <div
              className={styles.fsBackdrop}
              onClick={onClose}
            />
            <div className={styles.teacherFSSubView}>
              <div className={styles.container}>
                {(isTablet || isMobile) && showMenu && <div className={styles.fsMenudrop} onClick={() => setShowMenu(false)}/>}
                <div className={styles.titleView}>
                  {(isTablet || isMobile) && <button onClick={handleMenu} className={styles.menuButton} type={'button'}>
                    <div className={styles.menuWrapper}>
                      <MenuOutlined className={styles.burgerIcon}/>
                    </div>
                  </button>}
                  <button onClick={onClose} className={styles.closeButton} type={'button'}>
                    <div className={styles.menuWrapper}>
                      <FSBackIcon className={styles.closeIcon}/>
                    </div>
                  </button>
                  <div className={styles.titleSubView}>
                    <div className={styles.subTitle}>You are previewing {quiz.name}'s Assessments</div>
                  </div>
                </div>
                <div className={styles.page}>
                  {listSection}
                  <div className={styles.content}>
                    {selectedContent && <SelectedContent content={selectedContent} goBack={onClose} onExit={onClose} isPreview={isPreview} showBanner={false} isParentApp={false} />}
                  </div>
                </div>
              </div>
            </div>
          </div>         
        </div>
      )}
      {showQuizView && selectedContent ? (
        <div className={styles.teacherFSView}>
          <div className={styles.fsBackdrop} onClick={onClose} />
          <div className={styles.lessonFSSubView}>
            <SelectedContent
              content={selectedContent}
              goBack={handleCloseQuiz}
            />
          </div>
        </div>
      ) : null}
      {showExitModal && 
        <GenerateExitTicketLinkModal 
          open={showExitModal} onClose={() => setShowExitModal(false)} 
          onActiveExitTicket={handleActiveFormPreview} quizType={true} 
          currentQuiz={quiz as Quiz}
      />}
    </UnitModal>
  );
}

export default QuizPreviewModal;
